import { cn } from '@/lib/utils'
import type { TableState, TableShape, SeatLayout } from '@/lib/types/table'

interface TableShapeRendererProps {
  name: string
  seatsMax: number
  shape?: TableShape
  state: TableState
  guestName?: string
  isSelected: boolean
  combineGlow?: 'green' | 'red' | null
  isRejected?: boolean
  className?: string
  posOrderStatus?: 'none' | 'open' | 'in_progress' | 'paid'
  seatLayout?: SeatLayout
  /** Suppress all text labels (used for tables rendered inside a combination overlay) */
  hideLabel?: boolean
}

const stateStyles: Record<TableState, string> = {
  available: 'bg-[var(--color-bg-elevated)] border-[var(--color-accent-green)]/30 shadow-[0_0_12px_rgba(122,158,126,0.15)]',
  occupied: 'bg-[var(--color-accent-amber-dim)] border-[var(--color-accent-amber)]/40 shadow-[0_0_16px_rgba(232,168,56,0.2)]',
  reserved: 'bg-[var(--color-bg-elevated)] border-[var(--color-accent-amber)]/50 animate-pulse-glow',
  waitlist: 'bg-[var(--color-bg-elevated)] border-dashed border-orange-400/40 animate-pulse-glow',
  not_bookable: 'bg-[var(--color-bg-overlay)] border-[var(--color-border-subtle)] opacity-50',
}

function deriveDefaultShape(seatsMax: number): TableShape {
  if (seatsMax === 3 || seatsMax >= 5) return 'circle'
  return 'square'
}

function getShape(seatsMax: number, tableShape?: TableShape): { width: number; height: number; borderRadius: string } {
  const shape = tableShape ?? deriveDefaultShape(seatsMax)

  if (shape === 'circle') {
    if (seatsMax <= 2) return { width: 60, height: 60, borderRadius: '50%' }
    if (seatsMax <= 4) return { width: 76, height: 76, borderRadius: '50%' }
    if (seatsMax <= 6) return { width: 92, height: 92, borderRadius: '50%' }
    return { width: 108, height: 108, borderRadius: '50%' }
  }

  if (shape === 'rectangle') {
    if (seatsMax <= 2) return { width: 88, height: 52, borderRadius: 'var(--radius-lg)' }
    if (seatsMax <= 4) return { width: 104, height: 60, borderRadius: 'var(--radius-lg)' }
    if (seatsMax <= 6) return { width: 128, height: 68, borderRadius: 'var(--radius-lg)' }
    return { width: 148, height: 76, borderRadius: 'var(--radius-lg)' }
  }

  // square
  if (seatsMax <= 2) return { width: 56, height: 56, borderRadius: 'var(--radius-lg)' }
  if (seatsMax <= 4) return { width: 72, height: 72, borderRadius: 'var(--radius-lg)' }
  if (seatsMax <= 6) return { width: 88, height: 88, borderRadius: 'var(--radius-lg)' }
  return { width: 104, height: 104, borderRadius: 'var(--radius-lg)' }
}

const DOT_SIZE = 11

/** Evenly distribute N dots around the circle perimeter */
function circleSeats(n: number, w: number, h: number): { x: number; y: number }[] {
  const cx = w / 2
  const cy = h / 2
  const r = w / 2 + DOT_SIZE / 2 - 1
  return Array.from({ length: n }, (_, i) => {
    const angle = (2 * Math.PI / n) * i - Math.PI / 2
    return {
      x: cx + r * Math.cos(angle) - DOT_SIZE / 2,
      y: cy + r * Math.sin(angle) - DOT_SIZE / 2,
    }
  })
}

/** Evenly distribute N dots around a rectangle perimeter */
function rectSeats(n: number, w: number, h: number): { x: number; y: number }[] {
  const perimeter = 2 * (w + h)
  const spacing = perimeter / n
  const offset = DOT_SIZE / 2
  const dots: { x: number; y: number }[] = []

  for (let i = 0; i < n; i++) {
    const d = (spacing * i + spacing / 2) % perimeter
    let x: number
    let y: number

    if (d < w) {
      x = d - DOT_SIZE / 2
      y = -offset
    } else if (d < w + h) {
      x = w - offset
      y = (d - w) - DOT_SIZE / 2
    } else if (d < 2 * w + h) {
      x = (w - (d - w - h)) - DOT_SIZE / 2
      y = h - offset
    } else {
      x = -offset
      y = (h - (d - 2 * w - h)) - DOT_SIZE / 2
    }
    dots.push({ x, y })
  }
  return dots
}

/** Place dots per-side using explicit layout (top/bottom/left/right) */
function sideLayoutSeats(
  layout: SeatLayout,
  w: number,
  h: number,
): { x: number; y: number }[] {
  const offset = DOT_SIZE / 2
  const dots: { x: number; y: number }[] = []

  const place = (count: number, positions: () => { x: number; y: number }[]) => {
    if (count > 0) dots.push(...positions())
  }

  place(layout.top, () => {
    const step = w / (layout.top + 1)
    return Array.from({ length: layout.top }, (_, i) => ({
      x: step * (i + 1) - DOT_SIZE / 2,
      y: -offset,
    }))
  })

  place(layout.bottom, () => {
    const step = w / (layout.bottom + 1)
    return Array.from({ length: layout.bottom }, (_, i) => ({
      x: step * (i + 1) - DOT_SIZE / 2,
      y: h - offset,
    }))
  })

  place(layout.left, () => {
    const step = h / (layout.left + 1)
    return Array.from({ length: layout.left }, (_, i) => ({
      x: -offset,
      y: step * (i + 1) - DOT_SIZE / 2,
    }))
  })

  place(layout.right, () => {
    const step = h / (layout.right + 1)
    return Array.from({ length: layout.right }, (_, i) => ({
      x: w - offset,
      y: step * (i + 1) - DOT_SIZE / 2,
    }))
  })

  return dots
}

function SeatDots({
  seatsMax,
  shape,
  seatLayout,
  dims,
}: {
  seatsMax: number
  shape?: TableShape
  seatLayout?: SeatLayout
  dims: { width: number; height: number }
}) {
  const resolvedShape = shape ?? deriveDefaultShape(seatsMax)

  let dots: { x: number; y: number }[]
  if (resolvedShape === 'circle') {
    dots = circleSeats(seatsMax, dims.width, dims.height)
  } else if (seatLayout) {
    dots = sideLayoutSeats(seatLayout, dims.width, dims.height)
  } else {
    dots = rectSeats(seatsMax, dims.width, dims.height)
  }

  return (
    <>
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-[var(--color-bg-overlay)] border border-[var(--color-border-subtle)]"
          style={{ width: DOT_SIZE, height: DOT_SIZE, left: d.x, top: d.y, zIndex: 0 }}
        />
      ))}
    </>
  )
}

const POS_STATUS_DOT: Record<string, string> = {
  open: 'bg-green-400',
  in_progress: 'bg-amber-400',
  paid: 'bg-blue-400',
}

export function TableShapeRenderer({
  name,
  seatsMax,
  shape,
  state,
  guestName,
  isSelected,
  combineGlow,
  isRejected,
  className,
  posOrderStatus,
  seatLayout,
  hideLabel,
}: TableShapeRendererProps) {
  const dims = getShape(seatsMax, shape)

  return (
    <div className="relative" style={{ width: dims.width, height: dims.height }}>
      {/* Seat dots rendered behind the table */}
      <SeatDots seatsMax={seatsMax} shape={shape} seatLayout={seatLayout} dims={dims} />

      {/* Table shape */}
      <div
        className={cn(
          'absolute inset-0 flex flex-col items-center justify-center border-2 transition-colors duration-200 cursor-pointer select-none',
          stateStyles[state],
          // Selection ring
          !isRejected && isSelected && 'ring-2 ring-[var(--color-accent-amber)] ring-offset-2 ring-offset-[var(--color-bg-base)]',
          // Proximity combine glow (suppressed while rejected)
          !isRejected && combineGlow === 'green' && 'ring-2 ring-green-400/60 shadow-[0_0_20px_rgba(74,222,128,0.3)]',
          !isRejected && combineGlow === 'red' && 'ring-2 ring-red-400/60 shadow-[0_0_20px_rgba(248,113,113,0.3)]',
          // Rejection: red flash + shake — overrides everything else
          isRejected && 'animate-shake ring-2 ring-red-500/80 shadow-[0_0_24px_rgba(239,68,68,0.5)] bg-red-500/10',
          className,
        )}
        style={{
          borderRadius: dims.borderRadius,
          boxShadow: isRejected
            ? undefined
            : isSelected && state !== 'reserved' && state !== 'waitlist'
              ? '0 0 20px rgba(232,168,56,0.3)'
              : undefined,
        }}
      >
        {posOrderStatus && posOrderStatus !== 'none' && POS_STATUS_DOT[posOrderStatus] && (
          <span className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full ${POS_STATUS_DOT[posOrderStatus]}`} />
        )}
        {!hideLabel && (
          <>
            <span className="text-xs font-semibold text-[var(--color-text-primary)] leading-none">{name}</span>
            <span className="text-[10px] text-[var(--color-text-muted)] mt-0.5">{seatsMax}p</span>
            {guestName && state === 'occupied' && (
              <span className="text-[9px] text-[var(--color-accent-amber)] truncate max-w-[80%] mt-0.5">
                {guestName}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export { getShape }
